import { DateTime } from 'luxon'
import { isTrackRunning } from '../../api/selectors'
import { AdvancedTrackSummary } from '../../api/types'
import { DEFAULT_TRACK_MONTHS, MONTHS, TOTAL_WEEKS, TrackRow, WEEKS_PER_MONTH } from './categories'
import { PlacedItem } from './PlannerCell'

const toDateTime = (at: string | Date) => (typeof at === 'string' ? DateTime.fromISO(at) : DateTime.fromJSDate(at))

// Whole weeks from the anchor; an accepted start is a Monday, so it lands on a week boundary.
export const planWeekOf = (anchor: string, at: string | Date): number =>
  Math.max(0, Math.floor(toDateTime(at).diff(DateTime.fromISO(anchor, { zone: 'utc' }), 'weeks').weeks))

// Anything before Month 1, or with no Month 1 yet, counts as Month 1.
export const planMonthOf = (anchor: string | null, at: string | Date): number =>
  anchor ? Math.max(0, Math.floor(toDateTime(at).diff(DateTime.fromISO(anchor), 'months').months)) : 0

// 0 at the start of Month 1, 1 at the end of Month 6. The fraction is how far through the current month.
export const planOffset = (anchor: string, at: string | Date): number => {
  const anchorDt = DateTime.fromISO(anchor)
  const atDt = toDateTime(at)
  if (!anchorDt.isValid || !atDt.isValid || atDt.toMillis() <= anchorDt.toMillis()) {
    return 0
  }
  const monthIndex = planMonthOf(anchor, at)
  if (monthIndex >= MONTHS) {
    return 1
  }
  const monthStart = anchorDt.plus({ months: monthIndex })
  const monthEnd = anchorDt.plus({ months: monthIndex + 1 })
  const span = monthEnd.toMillis() - monthStart.toMillis()
  const fraction = span > 0 ? (atDt.toMillis() - monthStart.toMillis()) / span : 0

  return (monthIndex + Math.min(1, Math.max(0, fraction))) / MONTHS
}

// A test clock for plan week `week`: the middle of that week column, and a Month 1 to count it from.
export const simulatedPlanTime = (
  week: number,
  anchor: string | null,
  realNow: Date
): { anchor: string | null; now: Date } => {
  const monthOne = anchor ? DateTime.fromISO(anchor) : DateTime.fromJSDate(realNow).startOf('week')
  const month = Math.floor(week / WEEKS_PER_MONTH)
  const monthStart = monthOne.plus({ months: month })
  const span = monthOne.plus({ months: month + 1 }).toMillis() - monthStart.toMillis()
  const fraction = ((week % WEEKS_PER_MONTH) + 0.5) / WEEKS_PER_MONTH

  return { anchor: monthOne.toISO(), now: new Date(monthStart.toMillis() + fraction * span) }
}

export type PlanZoom = 'month' | 'week'

// Month view: the label column is a share of the table. Week view: fixed pixels. The line uses the same figures.
export const LABEL_COL_SHARE = 0.2
export const LABEL_COL_PX = 220
export const WEEK_COL_PX = 96

// Nothing in the future is running: a running track's bar moved past this week needs that run ended first.
export const runEndedByMove = (
  tracks: AdvancedTrackSummary[],
  item: Pick<PlacedItem, 'trackKey'>,
  toWeek: number,
  currentWeek: number
): AdvancedTrackSummary | undefined =>
  toWeek > currentWeek ? tracks.find((track) => track.trackKey === item.trackKey && isTrackRunning(track)) : undefined

const clampWeek = (week: number) => Math.min(Math.max(week, 0), TOTAL_WEEKS - 1)

export const shiftedMonth = (startMonth: number, steps: number): number =>
  Math.min(Math.max(startMonth + steps, 0), MONTHS - 1)

export const shiftedWeek = (startWeek: number, steps: number): number => clampWeek(startWeek + steps)

// Where "now" falls, in week columns from Month 1's left edge.
export const nowColumn = (anchor: string | null, now: Date): number =>
  anchor && toDateTime(now) >= DateTime.fromISO(anchor) ? planOffset(anchor, now) * TOTAL_WEEKS : 0

// The plan week a moment falls in, counted in whole weeks from Month 1 as accepted starts are.
export const planWeekAt = (anchor: string | null, at: string | Date): number =>
  anchor ? clampWeek(planWeekOf(anchor, at)) : 0

export const itemStartWeek = (item: Pick<PlacedItem, 'startMonth' | 'startWeek'>): number =>
  item.startWeek ?? item.startMonth * WEEKS_PER_MONTH

// The week a run was accepted to start, when that differs from the week its intake opened; a line on its bar.
export const acceptedStartWeek = (track: AdvancedTrackSummary, anchor: string | null): number | undefined => {
  if (!anchor || track.stage !== 'accepted' || !track.startedAt || track.endedAt) {
    return undefined
  }
  const startWeek = planWeekAt(anchor, track.startedAt)

  return startWeek === planWeekAt(anchor, track.openedAt) ? undefined : startWeek
}

export const nowLinePercent = (column: number, zoom: PlanZoom): number => {
  if (zoom === 'month') {
    return (LABEL_COL_SHARE + (1 - LABEL_COL_SHARE) * (column / TOTAL_WEEKS)) * 100
  }
  const timeline = TOTAL_WEEKS * WEEK_COL_PX
  return ((LABEL_COL_PX + column * WEEK_COL_PX) / (LABEL_COL_PX + timeline)) * 100
}

export const LIVE_ITEM_PREFIX = 'live-'

export const isLiveItem = (item: PlacedItem) => item.id.startsWith(LIVE_ITEM_PREFIX)

// An ended run belongs to a planned bar only if it ran during the bar's months, or there is no Month 1 yet.
export const endedRunOverlaps = (
  track: AdvancedTrackSummary,
  item: Pick<PlacedItem, 'startMonth' | 'months'>,
  anchor: string | null
): boolean => {
  if (!track.endedAt) {
    return false
  }
  if (!anchor) {
    return true
  }
  const began = planMonthOf(anchor, track.startedAt ?? track.openedAt)
  const ended = planMonthOf(anchor, track.endedAt)

  return began < item.startMonth + item.months && ended >= item.startMonth
}

export type DueSuggestion = {
  rowKey: string
  trackKey: string
  kind: 'start' | 'end'
  enrollmentId: string | null
}

// A planned bar whose start month has arrived and nothing is running, or whose end month has arrived and one still is.
export const dueSuggestions = (
  rows: { key: string; items: PlacedItem[] }[],
  tracks: AdvancedTrackSummary[],
  anchor: string | null,
  currentMonth: number | null
): DueSuggestion[] => {
  if (currentMonth == null) {
    return []
  }
  const seen = new Set<string>()

  return rows.flatMap((row) =>
    row.items.flatMap((item) => {
      if (!item.trackKey || isLiveItem(item)) {
        return []
      }
      const runs = tracks.filter((track) => track.trackKey === item.trackKey)
      const active = runs.find((track) => track.endedAt == null)
      const running = runs.find((track) => isTrackRunning(track))
      const endMonth = item.startMonth + item.months
      const overlaps = runs.some((track) => endedRunOverlaps(track, item, anchor))
      const startDue = !active && !overlaps && currentMonth >= item.startMonth && currentMonth < endMonth
      const endDue = Boolean(running) && currentMonth >= endMonth
      if (!startDue && !endDue) {
        return []
      }
      const kind = startDue ? 'start' : 'end'
      if (seen.has(`${kind}:${item.trackKey}`)) {
        return []
      }
      seen.add(`${kind}:${item.trackKey}`)

      return [
        {
          rowKey: row.key,
          trackKey: item.trackKey,
          kind,
          enrollmentId: kind === 'end' ? running?.enrollmentId ?? null : null,
        },
      ]
    })
  )
}

// Tracks the patient is or was on that this row's plan never placed, drawn from their real start.
export const unplannedItemsFor = (
  row: TrackRow,
  planned: PlacedItem[],
  tracks: AdvancedTrackSummary[],
  anchor: string | null,
  now: Date
): PlacedItem[] => {
  const plannedKeys = new Set(planned.flatMap((item) => (item.trackKey ? [item.trackKey] : [])))
  const lastMonth = MONTHS - 1

  return row.tracks.flatMap((definition) => {
    const track = tracks.find((candidate) => candidate.trackKey === definition.trackKey)
    if (!track || plannedKeys.has(track.trackKey)) {
      return []
    }
    const startMonth = Math.min(planMonthOf(anchor, track.startedAt ?? track.openedAt), lastMonth)
    const elapsedEnd = planMonthOf(anchor, track.endedAt ?? now)
    // A running track is drawn to where it's heading, not just the time it has run so far.
    const endMonth = Math.min(
      track.endedAt ? elapsedEnd : Math.max(elapsedEnd, startMonth + DEFAULT_TRACK_MONTHS - 1),
      lastMonth
    )

    return [
      {
        id: `${LIVE_ITEM_PREFIX}${track.enrollmentId}`,
        option: definition.title,
        trackKey: track.trackKey,
        startMonth,
        months: endMonth - startMonth + 1,
      },
    ]
  })
}
